/**
 * Centralized CTA (Call-to-Action) configuration for consistent styling
 * across all landing page sections
 */

export const CTA_STYLES = {
  // Primary CTA buttons (main conversion actions)
  primary: {
    padding: 'px-8 py-4',
    fontSize: 'text-lg',
    fontWeight: 'font-semibold',
    minWidth: 'min-w-[200px]',
    minHeight: 'min-h-[56px]',
    borderRadius: 'rounded-xl',
    gradient: 'from-emerald-600 to-cyan-600',
    hoverGradient: 'hover:from-emerald-700 hover:to-cyan-700',
    shadow: 'shadow-lg hover:shadow-xl',
    transform: 'hover:-translate-y-1',
  },
  
  // Secondary CTA buttons (supporting actions)
  secondary: {
    padding: 'px-6 py-3',
    fontSize: 'text-base',
    fontWeight: 'font-semibold',
    minWidth: 'min-w-[160px]',
    minHeight: 'min-h-[48px]',
    borderRadius: 'rounded-lg',
    gradient: 'from-emerald-500 to-cyan-500',
    hoverGradient: 'hover:from-emerald-600 hover:to-cyan-600',
    shadow: 'shadow-md hover:shadow-lg',
    transform: 'hover:-translate-y-0.5',
  },
  
  // Outline CTA buttons (less prominent actions)
  outline: {
    padding: 'px-8 py-4',
    fontSize: 'text-lg',
    fontWeight: 'font-semibold',
    minWidth: 'min-w-[200px]',
    minHeight: 'min-h-[56px]',
    borderRadius: 'rounded-xl',
    border: 'border-2 border-emerald-600',
    textColor: 'text-emerald-600',
    hoverBg: 'hover:bg-emerald-50',
    transform: 'hover:-translate-y-1',
  }
} as const;

export const CTA_MESSAGES = {
  primary: 'Get Your Free Forecast',
  trial: 'Start Free Trial',
  demo: 'Watch Live Demo',
  proPlan: 'Start Pro Trial',
  enterprise: 'Contact Sales',
  transformation: 'Start My Transformation',
  viewPricing: 'View Pricing Details'
} as const;

/**
 * Utility function to generate consistent CTA class strings
 */
export const getCTAClasses = (variant: keyof typeof CTA_STYLES, additionalClasses?: string) => {
  const styles = CTA_STYLES[variant];
  
  if (variant === 'outline') {
    const outlineStyles = styles as typeof CTA_STYLES.outline;
    return [
      'inline-flex items-center justify-center',
      'transition-all duration-300',
      'focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2',
      styles.padding,
      styles.fontSize,
      styles.fontWeight,
      styles.minWidth,
      styles.minHeight,
      styles.borderRadius,
      outlineStyles.border,
      outlineStyles.textColor,
      outlineStyles.hoverBg,
      styles.transform,
      additionalClasses
    ].filter(Boolean).join(' ');
  }
  
  const primaryStyles = styles as typeof CTA_STYLES.primary;
  const secondaryStyles = styles as typeof CTA_STYLES.secondary;
  
  return [
    'inline-flex items-center justify-center',
    'bg-gradient-to-r text-white',
    'transition-all duration-300',
    'focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2',
    styles.padding,
    styles.fontSize,
    styles.fontWeight,
    styles.minWidth,
    styles.minHeight,
    styles.borderRadius,
    primaryStyles.gradient || secondaryStyles.gradient,
    primaryStyles.hoverGradient || secondaryStyles.hoverGradient,
    primaryStyles.shadow || secondaryStyles.shadow,
    styles.transform,
    additionalClasses
  ].filter(Boolean).join(' ');
};
